import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, Globe, Lightbulb, ArrowLeft, Heart, Target, Sparkles } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

export default function AdsiDevelop() {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <LanguageSwitcher currentLang={i18n.language} onChangeLang={changeLanguage} />
            <Link to="/" className="p-1 border-2 border-black rounded-md hover:bg-black/5 transition-colors">
              <img 
                src="https://customer-assets.emergentagent.com/job_mindairy-site/artifacts/6hwqvtsj_mindary%20logo.jpeg" 
                alt="mindary logo" 
                className="w-10 h-10 object-contain"
              />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 mb-6">
              <Building2 className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold text-foreground tracking-tight mb-4" data-testid="adsi-title">
              {t('adsi.title')}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('adsi.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column - About */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                  {t('adsi.aboutUs')}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {t('adsi.aboutDesc1')}
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  {t('adsi.aboutDesc2')}
                </p>
              </div>

              {/* Values */}
              <div className="grid gap-4">
                <div className="flex items-start gap-4 p-4 bg-emerald-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{t('adsi.healthFirst')}</h3>
                    <p className="text-sm text-muted-foreground">{t('adsi.healthFirstDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{t('adsi.globalAccess')}</h3>
                    <p className="text-sm text-muted-foreground">{t('adsi.globalAccessDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{t('adsi.continuousDev')}</h3>
                    <p className="text-sm text-muted-foreground">{t('adsi.continuousDevDesc')}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column - Mindary */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-6"
            >
              <Card className="bg-white border-emerald-100 shadow-lg overflow-hidden">
                <div className="h-2 bg-gradient-to-r from-emerald-500 via-blue-500 to-orange-500" />
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
                      <Lightbulb className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-semibold text-foreground">{t('adsi.mindary')}</h3>
                      <p className="text-sm text-muted-foreground">{t('adsi.mindarySubtitle')}</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    {t('adsi.mindaryDesc1')}
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    {t('adsi.mindaryDesc2')}
                  </p>
                  <Link to="/last-ned">
                    <Button className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600">
                      <Sparkles className="w-4 h-4 mr-2" />
                      {t('adsi.tryMindary')}
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Vision */}
              <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground">{t('adsi.vision')}</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  {t('adsi.visionDesc')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
